import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { ListPagination } from "@/components/ListPagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Printer, ListChecks, FilePlus2, ArrowDownToLine } from "lucide-react";
import { inr, inr2, fmtDate } from "@/lib/format";
import { printInvoice, printInvoices } from "@/lib/print";
import ManualBillForm from "@/pages/ManualBillForm";
import SupplementaryBillForm from "@/pages/SupplementaryBillForm";
import { toast } from "sonner";

export const PresetChips = ({ presets, type, onPick }) => {
  const list = (presets || []).filter((p) => p.charge_type === type);
  if (!list.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((p, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onPick(String(p.amount))}
          className="rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600 transition-colors hover:border-primary hover:text-primary"
          data-testid={`preset-${type}-${p.amount}`}
        >
          ₹{Number(p.amount).toLocaleString("en-IN")}
        </button>
      ))}
    </div>
  );
};

export default function BillCreate() {
  const [customers, setCustomers] = useState([]);
  const [unbilled, setUnbilled] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [custCode, setCustCode] = useState("");
  const [selected, setSelected] = useState({});
  const [taxPercent, setTaxPercent] = useState(18);
  const [billingType, setBillingType] = useState("GST");
  const [unloadingCharge, setUnloadingCharge] = useState(0);
  const [haltingCharge, setHaltingCharge] = useState(0);
  const [loadingCharge, setLoadingCharge] = useState(0);
  const [labourUnionCharge, setLabourUnionCharge] = useState(0);
  const [loadingPointHalting, setLoadingPointHalting] = useState(0);
  const [chargePresets, setChargePresets] = useState([]);
  const [craneCharge, setCraneCharge] = useState(0);
  const [vehicleReturnCharge, setVehicleReturnCharge] = useState(0);
  const [extraDeliveryCharge, setExtraDeliveryCharge] = useState(0);
  const [packingCharge, setPackingCharge] = useState(0);
  const [billDescription, setBillDescription] = useState("");
  const [tpName, setTpName] = useState("");
  const [tpGst, setTpGst] = useState("");
  const [tpAddress, setTpAddress] = useState("");
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("consignments");
  const [billMode, setBillMode] = useState("separate");
  const [includeBasis, setIncludeBasis] = useState(false);
  const [rowCharges, setRowCharges] = useState({});

  const rc = (id) => rowCharges[id] || { unloading: 0, loading: 0, halting: 0, labour: 0, lph: 0, crane: 0, vreturn: 0, edelivery: 0, packing: 0 };
  const setRc = (id, key, val) => setRowCharges((s) => ({ ...s, [id]: { ...rc(id), [key]: val } }));
  const rowExtras = (id) => {
    const c = rc(id);
    return (Number(c.unloading) || 0) + (Number(c.loading) || 0) + (Number(c.halting) || 0) + (Number(c.labour) || 0) + (Number(c.lph) || 0) + (Number(c.crane) || 0) + (Number(c.vreturn) || 0) + (Number(c.edelivery) || 0) + (Number(c.packing) || 0);
  };
  const copyChargeToAll = (key) => {
    if (chosen.length === 0) return;
    const val = rc(chosen[0].id)[key] ?? 0;
    setRowCharges((s) => {
      const n = { ...s };
      chosen.forEach((r) => { n[r.id] = { ...rc(r.id), ...(n[r.id] || {}), [key]: val }; });
      return n;
    });
  };

  const load = async () => {
    try {
      const [c, u] = await Promise.all([api.get("/customers"), api.get("/reports/unbilled")]);
      setCustomers(c.data);
      setUnbilled(u.data.shipments);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
    api.get("/charge-presets").then((r) => setChargePresets(r.data || [])).catch(() => {});
  }, []);

  const location = useLocation();
  const bcaOf = (c) => {
    if (!c) return "";
    if (String(c.billing_customer_address || "").trim()) return c.billing_customer_address.trim();
    const part = String(c.address || "").split(",").map((s) => s.trim()).filter(Boolean).pop() || c.state || "";
    const pin = String(c.pincode || "").trim();
    return pin && part && !part.includes(pin) ? `${part}-${pin}` : part || pin;
  };
  useEffect(() => {
    if (!unbilled) return;
    const sid = new URLSearchParams(location.search).get("shipment");
    if (!sid) return;
    const sh = unbilled.find((s) => s.id === sid);
    if (sh) {
      setCustomerId(sh.customer_id || "");
      setSelected({ [sid]: true });
      setMode("consignments");
      toast.info(`Consignment ${sh.lr_no || sh.consignment_no || ""} pre-selected from Reminders`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unbilled === null]);

  const rows = useMemo(() => {
    if (!unbilled) return [];
    return unbilled.filter((s) => !customerId || s.customer_id === customerId);
  }, [unbilled, customerId]);

  const CN_PER_PAGE = 20;
  const [cnPageNo, setCnPageNo] = useState(1);
  useEffect(() => { setCnPageNo(1); }, [customerId, unbilled]);
  const cnPage = Math.min(cnPageNo, Math.max(1, Math.ceil(rows.length / CN_PER_PAGE)));
  const pagedRows = rows.slice((cnPage - 1) * CN_PER_PAGE, cnPage * CN_PER_PAGE);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const chosen = rows.filter((r) => selected[r.id]);
  const isThirdPartyRow = (r) =>
    !!r.third_party_name ||
    (r.gst_payable_by || "").toUpperCase() === "THIRD PARTY" ||
    (r.payable_by || "").toUpperCase() === "THIRD PARTY";
  const tpSource = chosen.find(isThirdPartyRow);
  const showThirdParty = !!tpSource;
  useEffect(() => {
    if (tpSource) {
      setTpName(tpSource.third_party_name || "");
      setTpGst(tpSource.third_party_gst || "");
      setTpAddress(tpSource.third_party_address || "");
    } else {
      setTpName("");
      setTpGst("");
      setTpAddress("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpSource?.id]);
  const isRcm = billingType === "RCM";
  const gstParty = (() => {
    const vals = Array.from(new Set(chosen.map((r) => r.gst_payable_by).filter(Boolean)));
    if (vals.length === 1) return vals[0];
    if (vals.length === 0) return "Consignee";
    return "as per consignment";
  })();
  const effectiveTax = isRcm ? 0 : Number(taxPercent) || 0;
  const freightSubtotal = chosen.reduce((s, r) => s + (r.freight_amount || 0), 0);
  const globalExtras = (Number(unloadingCharge) || 0) + (Number(haltingCharge) || 0) + (Number(loadingCharge) || 0) + (Number(labourUnionCharge) || 0) + (Number(loadingPointHalting) || 0) + (Number(craneCharge) || 0) + (Number(vehicleReturnCharge) || 0) + (Number(extraDeliveryCharge) || 0) + (Number(packingCharge) || 0);
  const perBillExtras = chosen.reduce((s, r) => s + rowExtras(r.id), 0);
  const separateMulti = billMode === "separate" && chosen.length > 1;
  const extras = separateMulti ? perBillExtras : globalExtras;
  const subtotal = freightSubtotal + extras;
  const tax = (subtotal * effectiveTax) / 100;
  const total = subtotal + tax;

  const createBill = async () => {
    if (chosen.length === 0) {
      toast.error("Select at least one consignment");
      return;
    }
    setSaving(true);
    try {
      const cust = customers.find((c) => c.id === customerId);
      const baseExtras = {
        unloading_charge: Number(unloadingCharge) || 0,
        halting_charge: Number(haltingCharge) || 0,
        loading_charge: Number(loadingCharge) || 0,
        labour_union_charge: Number(labourUnionCharge) || 0,
        loading_point_halting_charge: Number(loadingPointHalting) || 0,
        crane_charge: Number(craneCharge) || 0,
        vehicle_return_charge: Number(vehicleReturnCharge) || 0,
        extra_delivery_charge: Number(extraDeliveryCharge) || 0,
        packing_charge: Number(packingCharge) || 0,
      };

      const tpFields = showThirdParty
        ? { third_party_name: tpName, third_party_gst: tpGst, third_party_address: tpAddress }
        : {};

      if (billMode === "combine" || chosen.length === 1) {
        const res = await api.post("/bills", {
          customer_id: customerId,
          customer_name: cust ? cust.name : chosen[0].customer_name,
          shipment_ids: chosen.map((r) => r.id),
          tax_percent: effectiveTax,
          rcm: isRcm,
          include_basis: includeBasis,
          ...baseExtras,
          ...tpFields,
          bill_description: billDescription,
          billing_customer_address: bcaOf(cust || customers.find((cc) => cc.id === chosen[0].customer_id)),
        });
        toast.success(`Bill ${res.data.invoice_no} created (${inr(res.data.total)})`, {
          description: "Do you want to print it now?",
          duration: 9000,
          action: { label: "Print Bill", onClick: () => printInvoice(res.data, cust) },
        });
      } else {
        const created = [];
        for (let i = 0; i < chosen.length; i++) {
          const r = chosen[i];
          const c = rc(r.id);
          const rcCust = customers.find((cc) => cc.id === (r.customer_id || customerId));
          const res = await api.post("/bills", {
            customer_id: r.customer_id || customerId,
            customer_name: r.customer_name || (rcCust ? rcCust.name : ""),
            shipment_ids: [r.id],
            tax_percent: effectiveTax,
            rcm: isRcm,
            include_basis: includeBasis,
            unloading_charge: Number(c.unloading) || 0,
            loading_charge: Number(c.loading) || 0,
            halting_charge: Number(c.halting) || 0,
            labour_union_charge: Number(c.labour) || 0,
            loading_point_halting_charge: Number(c.lph) || 0,
            crane_charge: Number(c.crane) || 0,
            vehicle_return_charge: Number(c.vreturn) || 0,
            extra_delivery_charge: Number(c.edelivery) || 0,
            packing_charge: Number(c.packing) || 0,
            ...(isThirdPartyRow(r)
              ? {
                  third_party_name: r.third_party_name || tpName,
                  third_party_gst: r.third_party_gst || tpGst,
                  third_party_address: r.third_party_address || tpAddress,
                }
              : {}),
            bill_description: billDescription,
            billing_customer_address: bcaOf(rcCust),
          });
          created.push(res.data);
        }
        toast.success(`${created.length} separate bills created`, {
          description: created.map((b) => b.invoice_no).join(", "),
          duration: 12000,
          action: {
            label: "Print All",
            onClick: () => printInvoices(created, (b) => customers.find((c) => c.id === b.customer_id)),
          },
        });
        setRowCharges({});
      }
      setSelected({});
      setUnloadingCharge(0);
      setHaltingCharge(0);
      setLoadingCharge(0);
      setLabourUnionCharge(0);
      setLoadingPointHalting(0);
      setCraneCharge(0);
      setVehicleReturnCharge(0);
      setExtraDeliveryCharge(0);
      setBillDescription("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Bill Create"
        subtitle="Generate a freight bill (Tax Invoice) from unbilled consignments, or enter one manually."
      />

      <div className="mb-6 flex gap-2">
        <Button
          variant={mode === "consignments" ? "default" : "outline"}
          size="sm"
          className="gap-1.5"
          onClick={() => setMode("consignments")}
          data-testid="mode-consignments"
        >
          <ListChecks className="h-4 w-4" /> From Consignments
        </Button>
        <Button
          variant={mode === "manual" ? "default" : "outline"}
          size="sm"
          className="gap-1.5"
          onClick={() => setMode("manual")}
          data-testid="mode-manual"
        >
          <FilePlus2 className="h-4 w-4" /> Manual Bill
        </Button>
        <Button
          variant={mode === "supplementary" ? "default" : "outline"}
          size="sm"
          className="gap-1.5"
          onClick={() => setMode("supplementary")}
          data-testid="mode-supplementary"
        >
          <FilePlus2 className="h-4 w-4" /> Supplementary Bill
        </Button>
      </div>

      {mode === "supplementary" ? (
        <SupplementaryBillForm />
      ) : mode === "manual" ? (
        <ManualBillForm customers={customers} onCreated={load} />
      ) : (
      <>
      <div className="mb-4 flex flex-wrap items-end gap-4">
        <div className="grid gap-1.5">
          <Label>Customer Code</Label>
          <Input
            className="w-[140px]"
            value={custCode}
            placeholder="e.g. CUST-1001"
            data-testid="bill-customer-code"
            onChange={(e) => {
              const v = e.target.value;
              setCustCode(v);
              const key = v.trim().toLowerCase();
              const c = key ? customers.find((x) => (x.customer_code || "").toLowerCase() === key) : null;
              if (c) {
                setCustomerId(c.id);
                setSelected({});
              }
            }}
          />
        </div>
        <div className="grid gap-1.5">
          <Label>Filter by customer</Label>
          <Select
            value={customerId || "all"}
            onValueChange={(v) => {
              setCustomerId(v === "all" ? "" : v);
              setCustCode(v === "all" ? "" : customers.find((c) => c.id === v)?.customer_code || "");
              setSelected({});
            }}
          >
            <SelectTrigger className="w-[260px]" data-testid="bill-customer-filter">
              <SelectValue placeholder="All customers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All customers</SelectItem>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label>Billing Type</Label>
          <Select value={billingType} onValueChange={setBillingType}>
            <SelectTrigger className="w-[200px]" data-testid="bill-billing-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="GST">GST (add tax)</SelectItem>
              <SelectItem value="RCM">RCM (no GST)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label>Bill Mode</Label>
          <Select value={billMode} onValueChange={setBillMode}>
            <SelectTrigger className="w-[260px]" data-testid="bill-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="separate">Separate bill per consignment</SelectItem>
              <SelectItem value="combine">Combine into one bill</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-slate-50 px-3 py-2 text-sm" data-testid="bill-include-basis-label">
          <input type="checkbox" checked={includeBasis} onChange={(e) => setIncludeBasis(e.target.checked)} data-testid="bill-include-basis" className="h-4 w-4" />
          <span className="text-slate-600">Print payment basis (To-Pay / TBB) on bill</span>
        </label>
        {!isRcm && (
          <div className="grid gap-1.5">
            <Label>Tax %</Label>
            <Input type="number" className="w-[110px]" value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)} data-testid="bill-tax" />
          </div>
        )}
        {!separateMulti && (
        <>
        <div className="grid gap-1.5">
          <Label>Loading Charge (₹)</Label>
          <Input type="number" className="w-[150px]" value={loadingCharge} onChange={(e) => setLoadingCharge(e.target.value)} data-testid="bill-loading-charge" />
          <PresetChips presets={chargePresets} type="loading" onPick={setLoadingCharge} />
        </div>
        <div className="grid gap-1.5">
          <Label>Unloading Charge (₹)</Label>
          <Input type="number" className="w-[150px]" value={unloadingCharge} onChange={(e) => setUnloadingCharge(e.target.value)} data-testid="bill-unloading-charge" />
          <PresetChips presets={chargePresets} type="unloading" onPick={setUnloadingCharge} />
        </div>
        <div className="grid gap-1.5">
          <Label>Loading Point Halting Charge (₹)</Label>
          <Input type="number" className="w-[190px]" value={loadingPointHalting} onChange={(e) => setLoadingPointHalting(e.target.value)} data-testid="bill-loading-point-halting-charge" />
          <PresetChips presets={chargePresets} type="lph" onPick={setLoadingPointHalting} />
        </div>
        <div className="grid gap-1.5">
          <Label>Unloading Point Halting Charge (₹)</Label>
          <Input type="number" className="w-[190px]" value={haltingCharge} onChange={(e) => setHaltingCharge(e.target.value)} data-testid="bill-halting-charge" />
          <PresetChips presets={chargePresets} type="halting" onPick={setHaltingCharge} />
        </div>
        <div className="grid gap-1.5">
          <Label>Labour Union Charge (₹)</Label>
          <Input type="number" className="w-[170px]" value={labourUnionCharge} onChange={(e) => setLabourUnionCharge(e.target.value)} data-testid="bill-labour-union-charge" />
          <PresetChips presets={chargePresets} type="labour" onPick={setLabourUnionCharge} />
        </div>
        <div className="grid gap-1.5">
          <Label>Crane Charge (₹)</Label>
          <Input type="number" className="w-[150px]" value={craneCharge} onChange={(e) => setCraneCharge(e.target.value)} data-testid="bill-crane-charge" />
          <PresetChips presets={chargePresets} type="crane" onPick={setCraneCharge} />
        </div>
        <div className="grid gap-1.5">
          <Label>Vehicle Return Charge (₹)</Label>
          <Input type="number" className="w-[170px]" value={vehicleReturnCharge} onChange={(e) => setVehicleReturnCharge(e.target.value)} data-testid="bill-vehicle-return-charge" />
          <PresetChips presets={chargePresets} type="vreturn" onPick={setVehicleReturnCharge} />
        </div>
        <div className="grid gap-1.5">
          <Label>Extra Delivery Charge (₹)</Label>
          <Input type="number" className="w-[170px]" value={extraDeliveryCharge} onChange={(e) => setExtraDeliveryCharge(e.target.value)} data-testid="bill-extra-delivery-charge" />
          <PresetChips presets={chargePresets} type="edelivery" onPick={setExtraDeliveryCharge} />
        </div>
        <div className="grid gap-1.5">
          <Label>Packing Charge (₹)</Label>
          <Input type="number" className="w-[150px]" value={packingCharge} onChange={(e) => setPackingCharge(e.target.value)} data-testid="bill-packing-charge" />
          <PresetChips presets={chargePresets} type="packing" onPick={setPackingCharge} />
        </div>
        </>
        )}
      </div>

      {showThirdParty && (
        <Card className="mb-4 border-amber-300 bg-amber-50/50" data-testid="third-party-billing-card">
          <CardContent className="p-4">
            <div className="mb-3 text-sm font-semibold text-amber-800">Third Party Billing <span className="font-normal text-muted-foreground">— this consignment is payable by a third party; the bill will be raised on them</span></div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="grid gap-1.5">
                <Label>Third Party Name</Label>
                <Input value={tpName} onChange={(e) => setTpName(e.target.value)} placeholder="Third party name" data-testid="bill-third-party-name" />
              </div>
              <div className="grid gap-1.5">
                <Label>Third Party GST Number</Label>
                <Input value={tpGst} onChange={(e) => setTpGst(e.target.value)} placeholder="e.g. 33ABCDE1234F1Z5" data-testid="bill-third-party-gst" />
              </div>
              <div className="grid gap-1.5">
                <Label>Third Party Address</Label>
                <Input value={tpAddress} onChange={(e) => setTpAddress(e.target.value)} placeholder="Third party address" data-testid="bill-third-party-address" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-[44px]" />
              {["LR No.", "From Destination", "Customer Name", "Dispatch Date", "Freight"].map((h) => (
                <TableHead
                  key={h}
                  className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${h === "Freight" ? "text-right" : ""}`}
                >
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {unbilled === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {unbilled && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                  No unbilled consignments{customerId ? " for this customer" : ""}. Everything is billed. 🎉
                </TableCell>
              </TableRow>
            )}
            {pagedRows.map((r) => (
              <TableRow key={r.id} data-testid="bill-row" data-state={selected[r.id] ? "selected" : undefined}>
                <TableCell>
                  <Checkbox
                    checked={!!selected[r.id]}
                    onCheckedChange={() => toggle(r.id)}
                    data-testid={`bill-check-${r.id}`}
                  />
                </TableCell>
                <TableCell className="font-medium">{String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "") || "—"}</TableCell>
                <TableCell className="text-sm">
                  {r.origin} <span className="text-muted-foreground">→</span> {r.destination}
                </TableCell>
                <TableCell className="text-sm">{r.customer_name || "—"}</TableCell>
                <TableCell className="text-sm">{fmtDate(r.dispatch_date)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(r.freight_amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableShell>
      <ListPagination total={rows.length} page={cnPageNo} setPage={setCnPageNo} pageSize={CN_PER_PAGE} noun="unbilled consignments" testPrefix="billcn" />

      {separateMulti && (
        <Card className="mt-4 border-border" data-testid="per-bill-charges">
          <CardContent className="p-4">
            <div className="mb-3 text-sm font-semibold">Per-consignment charges <span className="font-normal text-muted-foreground">— each selected consignment gets its own bill with these charges</span></div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-[11px] uppercase tracking-[0.1em] text-slate-500">CN No.</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-[0.1em] text-slate-500">Freight</TableHead>
                    {[
                      { key: "unloading", label: "Unloading" },
                      { key: "loading", label: "Loading" },
                      { key: "halting", label: "Unld. Halting" },
                      { key: "labour", label: "Labour Union" },
                      { key: "lph", label: "Ld. Pt Halting" },
                      { key: "crane", label: "Crane" },
                      { key: "vreturn", label: "Veh. Return" },
                      { key: "edelivery", label: "Extra Dlv." },
                      { key: "packing", label: "Packing" },
                    ].map((col) => (
                      <TableHead key={col.key} className="text-[11px] uppercase tracking-[0.1em] text-slate-500">
                        <div className="flex items-center gap-1">
                          <span>{col.label}</span>
                          <button
                            type="button"
                            title={`Copy row 1's ${col.label} to all consignments`}
                            className="rounded p-0.5 text-primary hover:bg-primary/10"
                            onClick={() => copyChargeToAll(col.key)}
                            data-testid={`copy-${col.key}-all`}
                          >
                            <ArrowDownToLine className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </TableHead>
                    ))}
                    <TableHead className="text-[11px] uppercase tracking-[0.1em] text-slate-500">Bill Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chosen.map((r) => {
                    const c = rc(r.id);
                    const billTot = ((r.freight_amount || 0) + rowExtras(r.id)) * (1 + effectiveTax / 100);
                    const chargeInput = (key) => (
                      <Input
                        type="number"
                        className="h-8 w-[92px] text-sm"
                        value={c[key] ?? 0}
                        onChange={(e) => setRc(r.id, key, e.target.value)}
                        data-testid={`rowcharge-${key}-${r.id}`}
                      />
                    );
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap font-medium">{String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "") || "—"}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm tabular-nums">{inr(r.freight_amount)}</TableCell>
                        <TableCell>{chargeInput("unloading")}</TableCell>
                        <TableCell>{chargeInput("loading")}</TableCell>
                        <TableCell>{chargeInput("halting")}</TableCell>
                        <TableCell>{chargeInput("labour")}</TableCell>
                        <TableCell>{chargeInput("lph")}</TableCell>
                        <TableCell>{chargeInput("crane")}</TableCell>
                        <TableCell>{chargeInput("vreturn")}</TableCell>
                        <TableCell>{chargeInput("edelivery")}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm font-semibold tabular-nums" data-testid={`rowcharge-total-${r.id}`}>{inr2(billTot)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="mt-6 border-border">
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-6">
          <div className="w-full">
            <div className="grid gap-1.5">
              <Label>Description (prints above the items table, optional)</Label>
              <Textarea value={billDescription} onChange={(e) => setBillDescription(e.target.value)} placeholder="Type any description to show on the bill" data-testid="bill-description" rows={2} />
            </div>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex gap-6">
              <span className="text-muted-foreground">Selected consignments</span>
              <span className="font-medium tabular-nums" data-testid="bill-selected-count">{chosen.length}</span>
            </div>
            {billMode === "separate" && chosen.length > 1 && (
              <div className="text-xs font-medium text-primary" data-testid="bill-separate-note">
                Will generate {chosen.length} separate bills (one per consignment) — set each consignment's charges below.
              </div>
            )}
            <div className="flex gap-6">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="tabular-nums">{inr2(freightSubtotal)}</span>
            </div>
            {separateMulti && perBillExtras > 0 && (
              <div className="flex gap-6">
                <span className="text-muted-foreground">Extra charges (all consignments)</span>
                <span className="tabular-nums" data-testid="bill-perbill-extras">{inr2(perBillExtras)}</span>
              </div>
            )}
            {Number(unloadingCharge) > 0 && (
              <div className="flex gap-6">
                <span className="text-muted-foreground">Unloading Charge</span>
                <span className="tabular-nums" data-testid="bill-unloading-summary">{inr2(Number(unloadingCharge) || 0)}</span>
              </div>
            )}
            {Number(loadingCharge) > 0 && (
              <div className="flex gap-6">
                <span className="text-muted-foreground">Loading Charge</span>
                <span className="tabular-nums">{inr2(Number(loadingCharge) || 0)}</span>
              </div>
            )}
            {Number(loadingPointHalting) > 0 && (
              <div className="flex gap-6">
                <span className="text-muted-foreground">Loading Point Halting Charge</span>
                <span className="tabular-nums">{inr2(Number(loadingPointHalting) || 0)}</span>
              </div>
            )}
            {Number(haltingCharge) > 0 && (
              <div className="flex gap-6">
                <span className="text-muted-foreground">Unloading Point Halting Charge</span>
                <span className="tabular-nums" data-testid="bill-halting-summary">{inr2(Number(haltingCharge) || 0)}</span>
              </div>
            )}
            {Number(labourUnionCharge) > 0 && (
              <div className="flex gap-6">
                <span className="text-muted-foreground">Labour Union Charge</span>
                <span className="tabular-nums">{inr2(Number(labourUnionCharge) || 0)}</span>
              </div>
            )}
            {Number(craneCharge) > 0 && (
              <div className="flex gap-6">
                <span className="text-muted-foreground">Crane Charge</span>
                <span className="tabular-nums">{inr2(Number(craneCharge) || 0)}</span>
              </div>
            )}
            {Number(vehicleReturnCharge) > 0 && (
              <div className="flex gap-6">
                <span className="text-muted-foreground">Vehicle Return Charge</span>
                <span className="tabular-nums">{inr2(Number(vehicleReturnCharge) || 0)}</span>
              </div>
            )}
            {Number(extraDeliveryCharge) > 0 && (
              <div className="flex gap-6">
                <span className="text-muted-foreground">Extra Delivery Charge</span>
                <span className="tabular-nums">{inr2(Number(extraDeliveryCharge) || 0)}</span>
              </div>
            )}
            <div className="flex gap-6">
              <span className="text-muted-foreground">{isRcm ? `GST (RCM — payable by ${gstParty})` : `Tax (${taxPercent || 0}%)`}</span>
              <span className="tabular-nums">{inr2(tax)}</span>
            </div>
            <div className="flex gap-6 font-display text-lg font-bold">
              <span>Bill Total</span>
              <span className="tabular-nums" data-testid="bill-total">{inr2(total)}</span>
            </div>
          </div>
          <Button size="lg" onClick={createBill} disabled={saving || chosen.length === 0} data-testid="create-bill-btn">
            <FileText className="mr-2 h-4 w-4" />
            {saving ? "Creating…" : billMode === "separate" && chosen.length > 1 ? `Create ${chosen.length} Bills` : "Create Bill"}
          </Button>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}
